/**
 * The search grammar shared by the search box, smart groups and the extension popup.
 * Examples:
 *   company:acme title:designer
 *   location:"san francisco" lastmet>2026-01-01
 *   has:phone missing:birthday
 *   (group:investors [twitter:* linkedin:*])
 *   closest friends i emailed last month
 */
export const SEARCH_KEYWORDS = [
    "company",
    "title",
    "education",
    "interest",
    "location",
    "email",
    "phone",
    "emailcount",
    "meetingcount",
    "messagecount",
    "callcount",
    "firstmet",
    "lastmet",
    "firstevent",
    "lastevent",
    "nextmeeting",
    "firstemail",
    "lastemail",
    "firsttext",
    "lasttext",
    "created",
    "updated",
    "birthday",
    "is_local",
    "is_memorialized",
    "is_archived",
    "name",
    "bio",
    "notes",
    "source",
    "integration",
    "twitter",
    "linkedin",
    "facebook",
    "instagram",
    "group",
    "groupcount",
    "notedate",
    "lastnote",
    "lastcontact", // newest interaction of any type
    "relationship",
    "need", // Hub: needs text
    "service", // Hub: services text
    "category", // Hub: need/service category
    "hub", // Hub: is a Hub user (true/false)
];
export const SEARCH_KEYWORD_ALIASES = {
    org: "company",
    organization: "company",
    employer: "company",
    school: "education",
    interests: "interest",
    eventcount: "meetingcount",
    meetings: "meetingcount",
    emails: "emailcount",
    texts: "messagecount",
    displayname: "name",
    note: "notes",
    integrations: "integration",
    local: "is_local",
    memorialized: "is_memorialized",
    archived: "is_archived",
    x: "twitter",
    "x.com": "twitter",
    nextmeetingdate: "nextmeeting",
    lastinteraction: "lastcontact",
    lasttouch: "lastcontact",
    lasttalked: "lastcontact",
    tier: "relationship",
    needs: "need",
    services: "service",
    offers: "service",
};
export const SEARCH_EXISTENCE_TARGETS = [
    "email",
    "phone",
    "notes",
    "name",
    "location",
    "birthday",
    "company",
    "title",
    "website",
    "social",
    "avatar",
    "need",
    "service",
];
/** Relative date tokens accepted by date keywords. */
export const RELATIVE_DATE_TOKENS = [
    "today",
    "yesterday",
    "tomorrow",
    "thisweek",
    "nextweek",
    "lastweek",
    "thismonth",
    "lastmonth",
    "nextmonth",
    "thisquarter",
    "lastquarter",
    "thisyear",
    "lastyear",
    "7d",
    "14d",
    "30d",
    "90d",
    "180d",
    "1y",
];
export const SEARCH_HINTS = [
    { term: "company:", description: "People at a company", example: "company:acme" },
    { term: "title:", description: "Job title contains", example: "title:founder" },
    { term: "location:", description: "City or region", example: 'location:"new york"' },
    { term: "lastmet", description: "Last meeting date", example: "lastmet<90d" },
    { term: "lastemail", description: "Last email date", example: "lastemail>lastmonth" },
    { term: "lastcontact", description: "Last contact of any kind", example: "lastcontact<90d" },
    { term: "has:", description: "Has a field", example: "has:phone" },
    { term: "missing:", description: "Missing a field", example: "missing:birthday" },
    { term: "group:", description: "In a group", example: "group:investors" },
    { term: "relationship:", description: "Tier closest, closer, close", example: "relationship:closest" },
    { term: "need:", description: "Has a need mentioning", example: "need:accountant" },
    { term: "service:", description: "Offers a service mentioning", example: "service:design" },
    { term: "[ ]", description: "Any of", example: "[twitter:* linkedin:*]" },
];
/** Keyword labels for explanation chips. */
export const SEARCH_KEYWORD_LABELS = {
    company: "Company",
    title: "Title",
    education: "Education",
    interest: "Interest",
    location: "Location",
    email: "Email",
    phone: "Phone",
    emailcount: "Emails",
    meetingcount: "Meetings",
    messagecount: "Messages",
    callcount: "Calls",
    firstmet: "First met",
    lastmet: "Last met",
    firstevent: "First event",
    lastevent: "Last event",
    nextmeeting: "Next meeting",
    firstemail: "First email",
    lastemail: "Last email",
    firsttext: "First text",
    lasttext: "Last text",
    created: "Added",
    updated: "Updated",
    birthday: "Birthday",
    is_local: "Local only",
    is_memorialized: "Memorialized",
    is_archived: "Archived",
    name: "Name",
    bio: "Bio",
    notes: "Notes",
    source: "Source",
    integration: "Integration",
    twitter: "X",
    linkedin: "LinkedIn",
    facebook: "Facebook",
    instagram: "Instagram",
    group: "Group",
    groupcount: "Groups",
    notedate: "Note date",
    lastnote: "Last note",
    lastcontact: "Last contact",
    relationship: "Relationship",
    need: "Need",
    service: "Offer",
    category: "Category",
    hub: "On Hub",
};
/** A date value as words for a chip: "thisweek" is "this week", "90d" is "90 days ago", "+7d" is "7 days ahead". */
export function dateValueWords(value) {
    const v = value.trim().toLowerCase();
    const words = {
        today: "today",
        yesterday: "yesterday",
        tomorrow: "tomorrow",
        thisweek: "this week",
        lastweek: "last week",
        nextweek: "next week",
        thismonth: "this month",
        lastmonth: "last month",
        nextmonth: "next month",
        thisquarter: "this quarter",
        lastquarter: "last quarter",
        thisyear: "this year",
        lastyear: "last year",
    };
    if (words[v])
        return words[v];
    const ago = /^(\d+)([dy])$/.exec(v);
    if (ago)
        return `${ago[1]} ${ago[2] === "d" ? "day" : "year"}${ago[1] === "1" ? "" : "s"} ago`;
    const ahead = /^\+(\d+)d$/.exec(v);
    if (ahead)
        return `${ahead[1]} day${ahead[1] === "1" ? "" : "s"} ahead`;
    return value;
}
const RELATIVE_WORDS = {
    today: "today",
    yesterday: "yesterday",
    tomorrow: "tomorrow",
    "this week": "thisweek",
    "last week": "lastweek",
    "next week": "nextweek",
    "this month": "thismonth",
    "last month": "lastmonth",
    "next month": "nextmonth",
    "this quarter": "thisquarter",
    "last quarter": "lastquarter",
    "this year": "thisyear",
    "last year": "lastyear",
};
const RELATIVE_ALT = Object.keys(RELATIVE_WORDS).join("|");
/**
 * Natural phrases rewritten into grammar before tokenising, shared by the parser and by Ask Hub's question step.
 * Order matters: specific phrases first. The phone keeps the same table in SearchGrammar.swift.
 */
export const SEARCH_PHRASES = [
    {
        pattern: /\b(?:(?:people|contacts|anyone|everyone)\s+)?(?:i\s+)?(?:have\s*not|haven'?t|havent|not)\s+(?:seen|met|talked\s+to|spoken\s+to)\s+(?:in\s+)?(?:a\s+)?while\b/,
        replace: "lastmet<90d",
        chip: "Not seen in the last 90 days",
    },
    { pattern: /\b(?:(?:people|contacts)\s+)?(?:i'?ve?\s+|i\s+have\s+)?never\s+met\b/, replace: "meetingcount=0", chip: "Never met" },
    { pattern: /\b(?:i'?ve?\s+)?met\s+more\s+than\s+once\b/, replace: "meetingcount>1", chip: "Met more than once" },
    { pattern: /\b(?:i'?ve?\s+)?met\s+recently\b/, replace: "lastmet>30d", chip: "Met in the last 30 days" },
    {
        pattern: new RegExp(`\\b(?:(?:people|contacts)\\s+)?(?:i\\s+)?(emailed|texted|messaged|met)\\s+(${RELATIVE_ALT})\\b`),
        replace: (m) => `${m[1] === "emailed" ? "lastemail" : m[1] === "met" ? "lastmet" : "lasttext"}:${RELATIVE_WORDS[m[2]]}`,
        chip: (m) => `${m[1][0].toUpperCase()}${m[1].slice(1)} ${m[2]}`,
    },
    {
        pattern: /\bbirthdays?\s+(this\s+week|next\s+week|this\s+month|next\s+month|today|tomorrow)\b/,
        replace: (m) => `birthday:${m[1].replace(/\s+/g, "")}`,
        chip: (m) => `Birthday ${m[1]}`,
    },
    { pattern: /\bclosest(?:\s+(?:friends|people|contacts))?\b/, replace: "relationship:closest", chip: "Closest" },
    { pattern: /\bcloser(?:\s+(?:friends|people|contacts))?\b/, replace: "relationship:closer", chip: "Closer" },
    { pattern: /\bclose(?:\s+(?:friends|people|contacts))?\b(?![:=<>])/, replace: "relationship:close", chip: "Close" },
    {
        pattern: /\bc[\s-]?suite\b/,
        replace: "[title:ceo title:cto title:cfo title:coo title:cmo title:chief title:president title:founder]",
        chip: "C-suite titles",
    },
    {
        pattern: /\b(?:the\s+)?bay\s+area\b/,
        replace: '[location:"san francisco" location:oakland location:berkeley location:"palo alto" location:"san jose" location:"mountain view" location:"menlo park" location:"bay area"]',
        chip: "In the Bay Area",
    },
    { pattern: /\b(?:recently\s+added|new\s+(?:people|contacts))\b|^new$/, replace: "created>30d", chip: "Added in the last 30 days" },
    { pattern: /\b(?:hub\s+users?|on\s+hub)\b/, replace: "hub:true", chip: "Hub users" },
    { pattern: /\bwith\s+needs\b/, replace: "has:need", chip: "Has a need" },
    { pattern: /\b(?:offering|with)\s+(?:services|offers)\b/, replace: "has:service", chip: "Offers help" },
    { pattern: /\b(?:instagram|twitter)\s+mutuals\b/, replace: (m) => `${m[0].split(/\s+/)[0]}:*`, chip: (m) => `On ${m[0].split(/\s+/)[0]}` },
    { pattern: /(?<![:=<>]\s?)\blast\s+quarter\b/, replace: "lastmet:lastquarter", chip: "Met last quarter" },
];
function insideQuotes(text, index) {
    let quotes = 0;
    for (let i = 0; i < index; i++)
        if (text[i] === '"')
            quotes += 1;
    return quotes % 2 === 1;
}
/** First match that is not part of a keyword value (`relationship:closer`) or a quoted string. */
function findPhrase(pattern, text) {
    const re = new RegExp(pattern.source, pattern.flags.includes("g") ? pattern.flags : `${pattern.flags}g`);
    let m;
    while ((m = re.exec(text))) {
        if (m[0].length === 0) {
            re.lastIndex += 1;
            continue;
        }
        const before = m.index > 0 ? text[m.index - 1] : " ";
        if (!/[:=<>"\w-]/.test(before) && !insideQuotes(text, m.index))
            return m;
    }
    return null;
}
/** Rewrites the natural phrases in a lower-cased query into grammar and says what each one meant. */
export function rewriteSearchPhrases(query) {
    const chips = [];
    let out = query;
    for (const phrase of SEARCH_PHRASES) {
        const m = findPhrase(phrase.pattern, out);
        if (!m)
            continue;
        const replacement = typeof phrase.replace === "function" ? phrase.replace(m) : phrase.replace;
        chips.push(typeof phrase.chip === "function" ? phrase.chip(m) : phrase.chip);
        out = `${out.slice(0, m.index)} ${replacement} ${out.slice(m.index + m[0].length)}`;
    }
    return { text: out, chips };
}
//# sourceMappingURL=search.js.map